import React from "react";
import { useAdverts } from "./hook";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CustomLoading from "../../components/CustomLoading";
import { DatePicker } from "@mui/x-date-pickers";
import { FilterAlt, Search } from "@mui/icons-material";
import { format } from "date-fns";
import FilterModal from "./components/filter-modal";
import ReactPagination from "../../components/ReactPagination/ReactPagination";
import { useNavigate } from "react-router-dom";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function Adverts() {
  const {
    selectedProvinces,
    provinces,
    startDate,
    notFound,
    loading,
    endDate,
    filter,
    data,
    t,

    setSelectedProvinces,
    setCurrentPage,
    setSearchText,
    setStartDate,
    setEndDate,
    setFilter,
  } = useAdverts();

  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: "#f5f5f5" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: { xs: 2, lg: 5 },
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2, lg: 4 },
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",

            bgcolor: "white",
          }}
        >
          <img
            src="./assets/logo.png"
            alt="logo"
            style={{
              width: "calc(100vw * 0.15)",
              maxWidth: "120px",
            }}
          />
          <Typography variant="h5" fontWeight="bold">
            {t("open_adverts")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              sx={{ display: { lg: "none" } }}
              onClick={() => setFilter(true)}
            >
              <FilterAlt className="text-primary" />
            </IconButton>
            <LanguageSwitcher />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 2, lg: 5 },
            flexGrow: 1,
            px: { xs: 2, lg: 4 },
          }}
        >
          {/* Filter Section */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flexDirection: "column",
              gap: 2,
              width: { lg: "30%" },
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Tooltip title={t("Aramak için 3 karakter giriniz")}>
            <TextField
              size="small"
              placeholder={t("search")}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {},
              }}
            />
            </Tooltip>
            <FormControl fullWidth size="small">
              <InputLabel id="role-label">{t("select_province")}</InputLabel>
              <Select
                labelId="role-label"
                value={selectedProvinces}
                label={t("select_province")}
                onChange={(val) => {
                  const value = val.target.value;
                  if (value && typeof value === "number") {
                    setSelectedProvinces(value);
                  }
                }}
                renderValue={(val) => (
                  <Typography>
                    {provinces?.find((x) => x.provinceId === val)?.provinceName}
                  </Typography>
                )}
                MenuProps={MenuProps}
              >
                {provinces?.map((e) => (
                  <MenuItem key={e.provinceId} value={e.provinceId}>
                    <Checkbox
                      checked={selectedProvinces === e.provinceId}
                      size="small"
                    />
                    <ListItemText primary={e.provinceName} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider />
            <DatePicker
              label={t("start_date")}
              value={startDate}
              onChange={(date) => setStartDate(date ?? undefined)}
            />
            <DatePicker
              label={t("end_date")}
              value={endDate}
              onChange={(date) => setEndDate(date ?? undefined)}
            />
          </Box>

          {/* Advertisements Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              gap: 3,
              overflowY: "auto",
            }}
          >
            {loading ? (
              <CustomLoading />
            ) : notFound ? (
              <img
                src="./assets/not-found.png"
                alt="not found"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  margin: "auto",
                }}/>
            ) : (
              data?.items.map((x) => (
                <Box
                  key={x.id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                    p: 3,

                    bgcolor: "white",
                    gap: 3,
                    alignItems: { xs: "flex-start", sm: "center" },
                  }}
                >
                  {/* Job Info */}
                  <Box
                    sx={{
                      flex: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {x.jobDescription}
                    </Typography>
                    <Typography variant="body2">
                      {x.province.provinceName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {x.workingMethod}
                    </Typography>
                  </Box>

                  {/* Skills */}
                  <Box
                    sx={{
                      flex: 3,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {x.skills.map((e, index) => (
                      <Chip
                        key={index}
                        label={e}
                        sx={{
                          bgcolor: "#eeeefb",
                          color: "#5856d6",
                          fontSize: "12px",
                          borderRadius: "5px",
                        }}
                      />
                    ))}
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Chip label={x.experience} sx={{ borderRadius: "5px" }} />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/basvurudetay/${x.id}`)}
                    >
                      {t("apply")}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {format(x.createdAt, "dd.MM.yyyy")}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <ReactPagination
              className={`mb-5 ${
                (loading || notFound || data?.count === 0) && "hidden"
              }`}
              onPageChange={(page) => setCurrentPage(page)}
              pageCount={data?.pages ?? 0}
            />
          </Box>
        </Box>
      </Box>

      {/* Filter Modal */}
      {provinces && (
        <FilterModal
          onClose={() => setFilter(false)}
          startDate={startDate}
          endDate={endDate}
          provinces={provinces}
          selectedProvince={selectedProvinces}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          setSearchText={setSearchText}
          setSelectedProvince={setSelectedProvinces}
          open={filter ?? false}
        />
      )}
    </Box>
  );
}
